import {takeUntil} from 'rxjs/operators';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {Item, Project, Status} from '../../../project/shared/project-model';
import {SwitchBoardComponent} from '../../shared/model/component';
import {SessionStorage} from 'ngx-webstorage';
import {CommonModalComponent} from '../../../shared/common-modal/common-modal.component';
import {LoggerService} from '../../../shared/logging/logger.service';
import {ItemService} from '../../../project/shared/item.service';
import {OptionsPanelService} from '../../shared/services/options-panel.service';
import {PanelOptionWrapper} from '../../shared/model/panelOptionWrapper';
import {Opt, OptValue} from '../../shared/model/IProduct';
import {ComponentType} from '../../shared/model/component-type';
import {ConfigurationHelperService} from 'app/configuration/shared/services/configuration.helper.service';
import {ProjectService} from '../../../project/shared/project.service';
import {NavigationRoute} from '../../../shared/guards/route-enum';
import {Subject} from 'rxjs';
import {OptionsService} from '../../../shared/services/options.service';

@Component({
  standalone: false,
  selector: 'app-sld-options-component',
  templateUrl: './sld-options-component.component.html',
  styleUrls: ['./sld-options-component.component.less']
})
export class SldOptionsComponentComponent implements OnInit, OnDestroy, OnChanges {

  @SessionStorage()
  user;
  @SessionStorage()
  currentItemct: Item;
  @SessionStorage()
  currentProject: Project;
  savedValue: OptValue;
  savedOption: Opt;
  isAllowedToChange = false;
  panelOptionWrapper: PanelOptionWrapper;
  @Input() selectedComponent: SwitchBoardComponent;

  @Output() deleteComponent = new EventEmitter<PanelOptionWrapper>();
  @Output() moveCubicleEvent = new EventEmitter<{panelOptionWrapper: PanelOptionWrapper, direction: 'left' | 'right'}>();

  displayItems: {key: string, value: Opt[]}[] = [];
  unsubscribe$: Subject<void> = new Subject<void>();

  @ViewChild('changeDataModel') confirmChangeDataModal: CommonModalComponent;

  constructor(private logger: LoggerService,
              private optionPanelService: OptionsPanelService,
              private itemService: ItemService,
              private projectService: ProjectService,
              private optionsPanelService: OptionsPanelService,
              private configurationHelperService: ConfigurationHelperService,
              private optionsService: OptionsService) {
  }

  ngOnInit() {
    if (this.currentItemct.status !== Status.ordered &&
      !this.projectService.isReadOnlyProject(this.currentProject, this.user)) {
      this.isAllowedToChange = true;
    }
    this.optionsPanelService.getSelectedComponentInfos()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
      infos => { this.panelOptionWrapper = infos; });


  }

  ngOnChanges(): void {
    if (!this.selectedComponent) {
      return;
    }
    if (!this.isAllowedToChange) {
      this.selectedComponent.options.forEach(opt => { opt.visible = opt.values.length > 1 });
      this.displayItems = this.buildDisplayItemsForReadOnly(this.selectedComponent.options);
      return;
    }

    // Sync selectedComponent with category options
    this.optionsService.getOptionsByComponentId(this.selectedComponent.id).pipe(takeUntil(this.unsubscribe$)).subscribe(result => {
      const synced = new Set<string>();
      [...result.directOptions, ...result.categoryGroups.flatMap(g => g.options)].forEach(apiOpt => {
        const key = this.apiOptionKey(apiOpt);
        if (!synced.has(key)) {
          synced.add(key);
          this.syncItemOption(apiOpt);
        }
      });

      this.configurationHelperService.setVisibilityForDependentOptions(this.selectedComponent.options, this.selectedComponent);

      this.displayItems = [
        {key: '', value: result.directOptions.map(o => this.syncItemOption(o))},
        ...result.categoryGroups
          .sort((a, b) => a.category.categoryOrderIndex - b.category.categoryOrderIndex)
          .map(g => ({
          key: g.category.name,
          value: g.options.map(o => this.syncItemOption(o))
        }))
      ];
    });
  }

  public categoryHasOptions(options: Opt[]): boolean{
    return options.some(option => option.visible);
  }

  private buildDisplayItemsForReadOnly(options: Opt[]): {key: string, value: Opt[]}[] {
    const noCategory: Opt[] = [];
    const categoryMap = new Map<string, {key: string, value: Opt[]}>();
    const seen = new Set<string>();
    options.forEach(opt => {
      const key = this.apiOptionKey(opt);
      if (seen.has(key)) { return; }
      seen.add(key);
      if (!opt.category) {
        noCategory.push(opt);
      } else {
        if (!categoryMap.has(opt.category.name)) {
          categoryMap.set(opt.category.name, {key: opt.category.name, value: []});
        }
        categoryMap.get(opt.category.name).value.push(opt);
      }
    });
    return [{key: '', value: noCategory}, ...Array.from(categoryMap.values())];
  }

  //TODO: Remove this method when the API will return options with id and categoryId post options migration.
  private apiOptionKey(opt: Opt): string {
    return opt.id ? `${opt.id}::${opt.category?.id ?? ''}` : `name:${opt.name}`;
  }

  private syncItemOption(apiOpt: Opt): Opt {
    const existing = this.findItemOption(apiOpt);
    if (!existing) {
      if (apiOpt.values && apiOpt.values.length > 0) {
        this.selectedComponent.options.push(apiOpt);
      }
      return apiOpt;
    }
    existing.mandatory = apiOpt.mandatory;
    existing.disabled = apiOpt.disabled;
    existing.category = apiOpt.category;

    existing.values = apiOpt.values.map(value => {
      const existingValue = existing.values?.find(
        item => item.value === value.value
      );
      if (existingValue) {
        value.selected = existingValue.selected;
        value.quantity = existingValue.quantity;
        value.reference = existingValue.reference;
        value.referenceBom = existingValue.referenceBom;
      }
      return value;
    });
    if (existing.values.length === 1 && !existing.mandatory) {
      existing.values[0].selected = true;
    }
    return existing;
  }

  private findItemOption(apiOpt: Opt): Opt | undefined {
    return apiOpt.id
      ? this.selectedComponent.options.find(o =>
          o.id === apiOpt.id && (o.category?.id ?? null) === (apiOpt.category?.id ?? null))
      : this.selectedComponent.options.find(o => !o.id && o.name === apiOpt.name);
  }

  private hasMandatoryAttended(): boolean {
    return this.currentItemct.components.every(comp =>
      (comp.options as Opt[] ?? []).filter(opt => opt.mandatory && opt.values.length > 1)
        .every(opt => opt.values?.some(v => v.selected))
    );
  }

  closeParameters() {
    this.optionPanelService.setSelectedComponent(null);
    this.optionPanelService.updateSelectedComponentInfos(new PanelOptionWrapper(null, null, null));
  }

  /**
   * Just for the onChange from select element
   * Call the modal if need to be reinitialised
   * @param value
   * @param option
   */
  async onChangeCubicleOption(value, option) {
    if (this.currentItemct.status === Status.configured) {
      this.changeCubicleOption(value, option);
    } else {
      this.savedValue = value;
      this.savedOption = option;
      this.confirmChangeDataModal.show();
    }
  }

  /**
   * Change cubicle option method
   */
  async changeCubicleOption(value, option) {
    // manage the dependencies between options
    if (value && option && option.onPopUp) {
      this.configurationHelperService.manageDependentOptionsVisibility(value, option, this.selectedComponent);
    }
    option.values.forEach(val => { val.selected = val.value === value; });
    this.updateCubicleOption();
  }

  async onChangeToggleOption(isToAdd, option) {
    if ((isToAdd && option.values[0].reference) || (!isToAdd && !option.values[0].reference)) {
      option.values[0].selected = true;
      option.values[1].selected = false;
    } else {
      option.values[0].selected = false;
      option.values[1].selected = true;
    }
    this.updateCubicleOption();
  }

  async updateCubicleOption() {
    if (this.panelOptionWrapper.type === ComponentType.CUBICLE) {
      this.currentItemct.components[this.panelOptionWrapper.componentPosition] = this.selectedComponent;
    } else {
      this.currentItemct.components[this.panelOptionWrapper.componentPosition].functionalUnits
        [this.panelOptionWrapper.functionPosition].transformer = this.selectedComponent;
    }

    if (this.hasMandatoryAttended()) {
      this.currentItemct.maxNavigationStep = Math.max(
        this.currentItemct.maxNavigationStep,
        NavigationRoute.getNextStepByRange(this.currentItemct.range, this.currentItemct.currentNavigationStep).id
      );
    }

    await new Promise(resolve =>
      this.itemService.updateItemWithStatusChange(this.currentItemct, Status.configured, this.user, true)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(updatedItem => {
            updatedItem.selectedComponentIndex = this.currentItemct.selectedComponentIndex;
            this.currentItemct = updatedItem;
          }
        ));
    this.itemService.updateTotalPriceAndDimensions(this.unsubscribe$);
  }

  confirmLooseData() {
    this.currentItemct.maxNavigationStep = NavigationRoute.getNextStepByRange(this.currentItemct.range, this.currentItemct.currentNavigationStep).id;
    this.currentItemct.status = Status.configured;
    this.logger.logStatus(this.currentItemct);
    this.changeCubicleOption(this.savedValue, this.savedOption);
  }

  public moveCubicle(direction: 'left' | 'right') {
    this.moveCubicleEvent.emit({panelOptionWrapper: this.panelOptionWrapper, direction});
  }

  public fireDeleteModal() {
    this.deleteComponent.emit(this.panelOptionWrapper);
  }

  public showDeleteIcon(): boolean {
    return this.currentItemct.status !== Status.ordered && this.currentItemct.components.length !== 0 &&
      !this.projectService.isReadOnlyProject(this.currentProject, this.user);
  }

  noOptionsSelected(option) {
    return (option.values.findIndex(opt => opt.selected === true) === -1 || option.defaultValue === undefined);
  }

  ngOnDestroy() {
    // prevent memory leak when component destroyed
    this.unsubscribe$.next();
    // unsubscribe from the subject itself:
    this.unsubscribe$.unsubscribe();

  }

  /**
   * Function to intercept key press to manage deletion of cubicle and transformer by keyboard
   * @param {KeyboardEvent} event (containing the key pressed)
   */
  @HostListener('document:keydown', ['$event'])
  private handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Delete' && this.showDeleteIcon()) {
      this.fireDeleteModal();
    }
  }
}
